import { axiosIns } from "config/api.config";
import { ENV } from "config/ENV.config";

/** Short code for a catalog row: "HRM" from `code:"HRM"`, falling back to `appKey:"ERPBOI_HRM"`. */
const codeOf = (m: any): string =>
  String(m?.code ?? "").toUpperCase() ||
  String(m?.appKey ?? "").toUpperCase().replace(/^ERPBOI_/, "");

/**
 * Drop consoles this deployment does not offer (REACT_APP_MODULE_ALLOWLIST).
 *
 * Applied here rather than in each screen because four separate surfaces read a module list —
 * the Home hub, the login-page sidebar (@layout/AuthLayout), /pricing and /dashboard — and
 * filtering per-screen is how one of them gets missed. The login sidebar is the one that bites:
 * it advertises the whole suite right next to the password field.
 *
 * A no-op when the allowlist is empty, so every ErpBoi build is byte-identical in behaviour.
 */
const onlyAllowed = (res: any): any => {
  const allow = ENV.moduleAllowlist ?? [];
  if (!allow.length || !Array.isArray(res?.body)) return res;
  return { ...res, body: res.body.filter((m: any) => allow.includes(codeOf(m))) };
};

export const ModuleService = {
  // Public suite catalog (no auth) — visible apps for the home hub + login sidebar
  getPublicList: async (): Promise<any> => onlyAllowed(await axiosIns.get("/module/public/list")),
  // Public pricing (no auth) — visible apps + their PAYG rate for the /pricing page
  getPublicPricing: async (): Promise<any> =>
    onlyAllowed(await axiosIns.get(`/module/public/pricing?productCode=${ENV.productCode || "ERPBOI"}`)),
  getPurchasedModules: async (): Promise<any> => onlyAllowed(await axiosIns.get("/module/get-purchased-modules")),
  getAvailableModules: async (): Promise<any> => onlyAllowed(await axiosIns.get("/module/get-available-modules")),
  activateModule: async (payload): Promise<any> => await axiosIns.post("/module/activate-module", payload),
  getStore: async (): Promise<any> =>
    onlyAllowed(await axiosIns.get(`/module/store?productCode=${ENV.productCode || "ERPBOI"}`)),
  subscribe: async (moduleId: string): Promise<any> =>
    await axiosIns.post("/module/subscribe", { moduleId, productCode: ENV.productCode || "ERPBOI" }),
  cancel: async (moduleId: string): Promise<any> => await axiosIns.post("/module/cancel", { moduleId }),
};
